import { cn } from '@simplywallst/ui-core/next';
import { memo, useCallback, useRef, useState } from 'react';
import { curveStepAfter } from 'd3-shape';
import useResizeObserver from 'use-resize-observer';
import AreaClosed from '@visx/shape/lib/shapes/AreaClosed';
import LinePath from '@visx/shape/lib/shapes/LinePath';
import { useStyles, getChartCssVar } from './hooks/useNarrativeChartStyles';
import { usePriceHistoryData } from './hooks/usePriceHistoryData';
import { useMultipleNarrativeValuationHistory } from './hooks/useNarrativeValuationHistory';
import { ChartOverlay, type HoverParam } from './components/ChartOverlay';
import { useChartScales } from './hooks/useChartScales';
import { CHART } from './constants';
import {
  normalizeMultipleNarrativeChartData,
  type NormalisedDataPoint,
} from './utils';
import { getSelectedData, getLastData } from './utils';
import { getPeriodLabel } from './hooks/usePriceHistoryData/usePriceHistoryData';
import type { Narrative } from './hooks/useNarrativeChart';
import type { TabOnClickEvent } from '@simplywallst/ui-core';

// TODO: Duplicate into this project structure to prepare for Next.js migration
import Loader from '@assets/images/portfolio/loader.svg';
import { CompanyIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount/CompanyIntrinsicDiscount';

const ChartStatus = ({
  status,
  hasData,
}: {
  status: 'pending' | 'error' | 'success';
  hasData: boolean;
}) => {
  if (status === 'pending') {
    return (
      <div className="flex justify-center" role="status">
        <Loader className="block size-5 animate-spin self-center [&_*]:fill-softer" />
        &#8203;
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex justify-center text-xs" role="status">
        An error occurred.
      </div>
    );
  }

  if (status === 'success' && !hasData) {
    return (
      <div className="flex justify-center text-pretty text-xs" role="status">
        Sorry, there is currently no price data for this company.
      </div>
    );
  }

  return null;
};

type NarrativeChartProps = {
  selectedIndex?: number;
  companyId: string;
  currencyISO: string;
  narratives?: Narrative[];
};

const Tab = <Value extends string | number = string>({
  value,
  selected,
  disabled,
  hasPanel = true,
  className,
  children,
  onClick,
}: {
  value: Value;
  selected: boolean;
  disabled?: boolean;
  hasPanel?: boolean;
  className?: string;
  children: React.ReactNode;
  onClick: (event: TabOnClickEvent<Value>) => void;
}) => (
  <button
    role="tab"
    value={value}
    id={`${value}-tab`}
    aria-selected={Boolean(selected)}
    aria-controls={hasPanel ? `${value}-panel` : undefined}
    disabled={disabled ?? selected}
    onClick={onClick}
    className={className}
  >
    {children}
  </button>
);

const PeriodLinks = ({
  selectedPeriod,
  setSelectedPeriod,
  setIsMarkerPinned,
}: {
  selectedPeriod: (typeof CHART.PERIODS)[number];
  setSelectedPeriod: (period: (typeof CHART.PERIODS)[number]) => void;
  setIsMarkerPinned: (isMarkerPinned: boolean) => void;
}) => (
  <div className="grid max-w-[350px] grid-flow-col">
    {CHART.PERIODS.map((period) => (
      <Tab
        key={`price-chart-tab-${period}`}
        value={period}
        selected={selectedPeriod === period}
        className={cn(
          'group/tab block px-3.5 py-1.5 text-xs',
          selectedPeriod === period && 'rounded bg-surface text-solid',
          selectedPeriod !== period &&
            'text-softer hover:text-solid focus:text-solid'
        )}
        onClick={() => {
          if (period !== selectedPeriod) {
            setIsMarkerPinned(false);
          }
          setSelectedPeriod(period);
        }}
      >
        <div className="sr-only">{getPeriodLabel(period)}</div>
        <div aria-hidden="true">{period}</div>
      </Tab>
    ))}
  </div>
);

const MemoizedPeriodLinks = memo(PeriodLinks);

const StaticHeader = ({
  lastData,
  priceChange,
  isLoading,
  children,
}: {
  lastData: ReturnType<typeof getLastData>;
  priceChange: React.ReactNode;
  isLoading: boolean;
  children: React.ReactNode;
}) => (
  <div className="flex flex-wrap gap-x-6">
    <div className="min-w-[34ch]">
      <div className="flex items-center text-lg">
        {isLoading ? (
          <div className="flex">
            <Loader className="block size-5 animate-spin self-center [&_*]:fill-softer" />
            &#8203;
          </div>
        ) : (
          <>
            <span className="mr-1.5 inline-block size-1.5 shrink-0 rounded-full bg-[--chart-price-color]" />
            <div>{lastData.price}</div>
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-x-2 text-tiny">
        <div className="whitespace-nowrap text-[--chart-price-color]">
          {isLoading ? <>&#8203;</> : priceChange}
        </div>
        {!isLoading && children}
      </div>
    </div>
    {lastData.fairValues[lastData.fairValueIndex] && (
      <div>
        <div className="flex items-center text-lg">
          <span className="mr-1.5 inline-block size-1.5 shrink-0 rounded-full bg-[--chart-valuation-color]" />
          {lastData.fairValues[lastData.fairValueIndex]}
        </div>
        <div className="text-tiny text-softer">Fair Value</div>
      </div>
    )}
  </div>
);

const InteractiveHeader = ({
  selectedData,
  isLoading,
  selectedPriceChange,
  children,
}: {
  selectedData: ReturnType<typeof getSelectedData>;
  isLoading: boolean;
  selectedPriceChange: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="flex flex-wrap gap-x-8 gap-y-2 overflow-y-hidden overflow-x-scroll pr-3.5 scrollbar-hidden">
    <div className="flex gap-2">
      <div className="grid min-w-[37ch]">
        <div className="flex items-center text-lg">
          {isLoading ? (
            <div className="flex">
              <Loader className="block size-5 animate-spin self-center [&_*]:fill-softer" />
              &#8203;
            </div>
          ) : (
            <>
              <span className="mr-1.5 inline-block size-1.5 shrink-0 rounded-full bg-[--chart-price-color]" />
              <div>{selectedData.price}</div>
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-x-2 text-tiny">
          <div className="text-[--chart-price-color]">
            {isLoading ? <>&#8203;</> : selectedPriceChange}
          </div>
          {!isLoading && children}
        </div>
      </div>
      {selectedData.fairValues[selectedData.fairValueIndex] && (
        <div className="flex flex-col gap-x-4">
          <div className="flex items-center text-lg">
            <span className="mr-1.5 inline-block size-1.5 shrink-0 rounded-full bg-[--chart-valuation-color]" />
            {selectedData.fairValues[selectedData.fairValueIndex]}
          </div>
          <div className="text-tiny text-softer">Fair Value</div>
        </div>
      )}
    </div>
  </div>
);

const MemoizedStaticHeader = memo(StaticHeader);
const MemoizedInteractiveHeader = memo(InteractiveHeader);

export const NarrativeChart = ({
  selectedIndex,
  narratives = [],
  companyId,
  currencyISO,
}: NarrativeChartProps) => {
  const [selectedPeriod, setSelectedPeriod] = useState<
    (typeof CHART.PERIODS)[number]
  >(CHART.DEFAULT_SELECTED_PERIOD);
  const [markerData, setMarkerData] = useState<HoverParam | null>(null);
  const [isMarkerPinned, setIsMarkerPinned] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const {
    data: priceHistory,
    periodMap,
    status,
    isNegativeMovement,
  } = usePriceHistoryData(companyId, selectedPeriod);

  const chartStyles = useStyles({
    isNegativeMovement,
    isLoading: false,
    hasStuckTooltip: false,
  });

  const size = useResizeObserver<HTMLDivElement>({ ref: wrapperRef });

  const chartData = normalizeMultipleNarrativeChartData(
    priceHistory.entities,
    priceHistory.keys,
    narratives,
    'average'
  );

  const { xScale, yScale } = useChartScales({
    width: size.width ?? 0,
    height: size.height ?? 0,
    keys: priceHistory.keys,
    periodStart: periodMap[selectedPeriod],
    periodEnd: periodMap.NOW,
    normalisedEntities: chartData.normalisedEntities,
    normalisedValuationsByNarrative: chartData.normalisedValuationsByNarrative,
  });

  const narrativeValuationHistories = useMultipleNarrativeValuationHistory({
    keys: priceHistory.keys,
    narrativeValuations: chartData.normalisedValuationsByNarrative,
    xScale,
    period: selectedPeriod,
  });

  const handleSelect = useCallback(
    (index: number) => {
      const fairValues = narrativeValuationHistories.map(
        (history) =>
          history?.valuationsByDate[priceHistory.keys[index]]?.originalValue ||
          null
      );
      setMarkerData({
        time: priceHistory.keys[index],
        price:
          chartData.normalisedEntities[priceHistory.keys[index]]
            ?.originalValue || null,
        fairValues,
      });
    },
    [
      chartData.normalisedEntities,
      priceHistory.keys,
      narrativeValuationHistories,
    ]
  );

  const lastPriceKey = priceHistory.keys[priceHistory.keys.length - 1];
  const lastPrice = chartData.normalisedEntities[lastPriceKey];

  const lastFairValues = narrativeValuationHistories.map(
    (history) =>
      history?.valuationsByDate[lastPriceKey] as NormalisedDataPoint | null
  );

  const narrativeLabels = narratives.map(
    (narrative, index) =>
      narrative?.owner?.displayName || `Narrative ${index + 1}`
  );

  const lastData = getLastData({
    selectedIndex,
    lastPrice,
    currencyISO,
    lastFairValues,
    yScale,
    xScale,
    lastPriceKey,
  });

  const selectedData = getSelectedData({
    selectedIndex,
    markerData,
    narratives,
    lastPrice,
    currencyISO,
    xScale,
    yScale,
    narrativeValuationHistories,
    chartData,
  });

  const firstPrice =
    chartData.normalisedEntities[priceHistory.keys[0]]?.originalValue ?? 0;

  const showIntrinsicDiscount = lastFairValues.some((fv) =>
    Boolean(fv?.originalValue)
  );

  const isLoading = status === 'pending';

  return (
    <figure
      className="grid gap-2 @container/chart"
      style={chartStyles.containerStyles}
      onMouseLeave={() => !isMarkerPinned && setMarkerData(null)}
    >
      {/* Header: Small screens */}
      <div className="grid gap-3 md:hidden">
        <MemoizedPeriodLinks
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
          setIsMarkerPinned={setIsMarkerPinned}
        />
        <MemoizedInteractiveHeader
          selectedData={selectedData}
          isLoading={isLoading}
          selectedPriceChange={
            <div className="flex items-center gap-0.5">
              <svg
                viewBox="0 0 12 12"
                className="size-3 shrink-0"
                aria-hidden="true"
              >
                <path
                  d={
                    isNegativeMovement
                      ? 'M3 3 L9 9 M9 9 L9 3 M9 9 L3 9'
                      : 'M3 9 L9 3 M9 3 L9 9 M9 3 L3 3'
                  }
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
              {`${Math.abs(
                (selectedData.priceNumber ?? 0) - firstPrice
              ).toFixed(2)} (${(
                (((selectedData.priceNumber ?? 0) - firstPrice) / firstPrice) *
                100
              ).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}%)`}
            </div>
          }
        >
          {showIntrinsicDiscount && (
            <div className="text-tiny [&_*]:text-softer">
              <CompanyIntrinsicDiscount
                npvPerShare={
                  narratives[selectedData.fairValueIndex]?.company
                    ?.analysisValue?.npvPerShare ?? null
                }
                lastSharePrice={selectedData.priceNumber}
                fairValue={
                  selectedData.fairValueNumbers[selectedData.fairValueIndex]
                }
              />
            </div>
          )}
        </MemoizedInteractiveHeader>
      </div>

      {/* Header: Large screen */}
      <div className="flex items-center justify-between gap-4 pr-3.5 max-md:hidden">
        <MemoizedStaticHeader
          lastData={lastData}
          isLoading={isLoading}
          priceChange={
            <div className="flex items-center gap-0.5">
              <svg
                viewBox="0 0 12 12"
                className="size-3 shrink-0"
                aria-hidden="true"
              >
                <path
                  d={
                    isNegativeMovement
                      ? 'M3 3 L9 9 M9 9 L9 3 M9 9 L3 9'
                      : 'M3 9 L9 3 M9 3 L9 9 M9 3 L3 3'
                  }
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
              {`${Math.abs(
                (lastPrice?.originalValue ?? 0) - firstPrice
              ).toFixed(2)} (${(
                (((lastPrice?.originalValue ?? 0) - firstPrice) / firstPrice) *
                100
              ).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}%)`}
            </div>
          }
        >
          {showIntrinsicDiscount && (
            <div className="whitespace-nowrap text-tiny [&_*]:text-softer">
              <CompanyIntrinsicDiscount
                npvPerShare={
                  narratives[lastData.fairValueIndex]?.company?.analysisValue
                    ?.npvPerShare ?? null
                }
                lastSharePrice={lastPrice?.originalValue ?? 0}
                fairValue={
                  lastFairValues[lastData.fairValueIndex]?.originalValue ?? 0
                }
              />
            </div>
          )}
        </MemoizedStaticHeader>
        <MemoizedPeriodLinks
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
          setIsMarkerPinned={setIsMarkerPinned}
        />
      </div>

      {/* Chart container */}
      <div
        ref={wrapperRef}
        className="group/chart relative z-0 mx-[calc(var(--chart-padding)*-1)] aspect-[--chart-aspect-ratio,2/1] select-none overflow-hidden"
      >
        <div className="absolute inset-0 mx-[--chart-padding] place-content-center empty:hidden">
          <ChartStatus status={status} hasData={priceHistory.keys.length > 0} />
        </div>
        {status === 'success' && priceHistory.keys.length > 0 && (
          <>
            <svg
              y="0"
              x="0"
              width={size.width ?? 0}
              height={size.height ?? 0}
              aria-label="Narrative price chart"
              role="img"
              className="pointer-events-none absolute bottom-0 -z-1 touch-none"
            >
              {/* Price line */}
              <LinePath
                data={priceHistory.keys}
                x={selectedData.getX}
                y={selectedData.getPriceY}
                stroke={getChartCssVar('PRICE_COLOR')}
                strokeLinecap="butt"
                strokeWidth="2"
              />
              {/* Fair value lines and gradients for each narrative */}
              {selectedData.getFairValueYs.map((getFairValueY, index) => (
                <g key={`narrative-${index}`}>
                  {/* Fair value line */}
                  <LinePath
                    curve={curveStepAfter}
                    data={
                      narrativeValuationHistories[index]?.valuationTimestamps
                    }
                    x={selectedData.getX}
                    y={getFairValueY}
                    strokeDasharray={
                      selectedData.fairValueIndex === index ? '0' : '3, 4'
                    }
                    strokeLinecap="butt"
                    strokeWidth={selectedData.fairValueIndex === index ? 2 : 1}
                    stroke={
                      selectedData.fairValueIndex === index
                        ? getChartCssVar('VALUATION_COLOR')
                        : getChartCssVar('POINT_STROKE')
                    }
                    opacity={selectedData.fairValueIndex === index ? 1 : 0.4}
                    fill="none"
                  />
                  {/* Fair value gradient */}
                  {selectedData.fairValueIndex === index && (
                    <AreaClosed
                      curve={curveStepAfter}
                      data={
                        narrativeValuationHistories[index]?.valuationTimestamps
                      }
                      yScale={yScale}
                      x={selectedData.getX}
                      y={getFairValueY}
                      fill={`url(#chart-valuation-gradient)`}
                      fillOpacity="0.075"
                      y0={() => size.height ?? 0}
                    />
                  )}
                </g>
              ))}
              {/* Price gradient */}
              <AreaClosed
                data={priceHistory.keys}
                x={selectedData.getX}
                y={selectedData.getPriceY}
                yScale={yScale}
                fill="url(#chart-trend-gradient)"
                fillOpacity="0.9"
                y0={() => size.height ?? 0}
              />
            </svg>

            <ChartOverlay
              height={size.height ?? 0}
              isMarkerPinned={isMarkerPinned}
              keys={priceHistory.keys}
              lastData={lastData}
              selectedData={selectedData}
              narrativeLabels={narrativeLabels}
              xScale={xScale}
              showPriceBlips={selectedPeriod === '7D'}
              onClick={() => setIsMarkerPinned((p) => !p)}
              onSelect={handleSelect}
              intrinsicDiscount={
                showIntrinsicDiscount && (
                  <div className="text-tiny [&_*]:text-softer">
                    <CompanyIntrinsicDiscount
                      npvPerShare={
                        narratives[0]?.company?.analysisValue?.npvPerShare ??
                        null
                      }
                      lastSharePrice={selectedData.priceNumber}
                      fairValue={
                        selectedData.fairValueNumbers[
                          selectedData.fairValueIndex
                        ]
                      }
                    />
                  </div>
                )
              }
            />
          </>
        )}
      </div>

      {/* Shared svg elements */}
      <svg className="invisible absolute size-0 touch-none">
        <defs>
          <linearGradient
            id="chart-valuation-gradient"
            x1="0"
            x2="0"
            y1="0"
            y2="1"
          >
            <stop
              offset="0%"
              stopColor={getChartCssVar('VALUATION_COLOR')}
              stopOpacity="1"
            />
            <stop
              offset="30%"
              stopColor={getChartCssVar('PRICE_COLOR')}
              stopOpacity="0"
            />
          </linearGradient>
          <linearGradient id="chart-trend-gradient" x1="0" x2="0" y1="0" y2="1">
            <stop
              offset="0%"
              stopColor={getChartCssVar('PRICE_COLOR')}
              stopOpacity="0.25"
            />
            <stop
              offset="90%"
              stopColor={getChartCssVar('PRICE_COLOR')}
              stopOpacity="0"
            />
          </linearGradient>
        </defs>
      </svg>

      {/* Background gradient */}
      <div
        className={cn(
          'pointer-events-none absolute left-0 top-0 aspect-1 w-3/4 select-none bg-[linear-gradient(140deg,var(--chart-price-color)_0%,transparent_40%)] opacity-[.025] transition-opacity delay-700 duration-1000 ease-out',
          isLoading && 'opacity-0'
        )}
      />
    </figure>
  );
};
